import { Component, OnInit, Input, OnDestroy, HostBinding, Optional, Self, ElementRef, DoCheck } from '@angular/core';
import { ControlValueAccessor, FormBuilder, Validators, FormGroup, NgControl, NgForm, FormGroupDirective } from '@angular/forms';
import { MatFormFieldControl, CanUpdateErrorState, ErrorStateMatcher } from '@angular/material';
import { FocusMonitor } from '@angular/cdk/a11y';
import { Subject } from 'rxjs';
import { untilDestroyed } from 'ngx-take-until-destroy';

import { NumberValidator } from '@awp/forms';
import { DateUtils } from '@awp/core/utils';

import { _AwpComponentMixinBase } from '../custom-base.component';

@Component({
  selector: 'awp-date-input',
  templateUrl: './date-input.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: DateInputComponent
    }
  ]
})
export class DateInputComponent extends _AwpComponentMixinBase implements
  MatFormFieldControl<Date>, DoCheck, OnInit, CanUpdateErrorState, ControlValueAccessor, OnDestroy {

  static nextId = 0;
  @HostBinding() id = `awp-date-input-${DateInputComponent.nextId++}`
  @HostBinding('attr.aria-describedby') describedBy = '';
  controlType = 'aws-date-input';

  readonly stateChanges: Subject<void> = new Subject<void>();
  focused = false;

  form: FormGroup;
  private _onTouched: any;
  private _onChange: (value: Date) => void;
  private _placeholder: string;
  private _required = false;
  private _disabled = false;
  private _value: Date = null;

  // tslint:disable-next-line: no-input-rename
  @Input() day: number;
  @Input() month: number;
  @Input() year: number;

  @Input() dayPlaceHolder: string | '' = '';
  @Input() monthPlaceHolder: string | '' = '';
  @Input() yearPlaceHolder: string | '' = '';

  constructor(
    @Optional() @Self() ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    private formBuilder: FormBuilder,
    private focusMonitor: FocusMonitor,
    private elRef: ElementRef<HTMLElement>) {

    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.focusMonitor
      .monitor(elRef.nativeElement, true)
      .pipe(untilDestroyed(this))
      .subscribe(origin => {
        this.focused = !!origin;
        this.stateChanges.next();
      });

    this.form = this.formBuilder.group({
        day: [null, [Validators.required, Validators.pattern(/[0-9]{1,2}/)]],
        month: [null, [Validators.required, Validators.pattern(/[0-9]{1,2}/), NumberValidator.range(1, 12)]],
        year: [null, [Validators.required, Validators.pattern(/[12][0-9]{3}/)]],
      },
      {
        updateOn: 'blur'
      });
  }

  ngOnInit() {
    // pad to 2 digits
    for (const controlName of ['day', 'month']) {
      const control = this.form.get(controlName);
      control.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(value => {
          if (value && value.length === 1) {
            control.setValue(value.padStart(2, '0'));
          }
        });
    }

    this.form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(p => {
        this.notifyChanged(p);
      });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elRef.nativeElement);
  }

  ngDoCheck(): void {
    if (this._onTouched != null) {
      // if you tab past all three date controls, then mark the control as touched
      if (this.form.get('day').touched && (this.form.get('month').touched && this.form.get('year').touched)) {
        this._onTouched();
      }
    }

    if (this.ngControl != null) {
      // triggers validation
      this.updateErrorState();
    }
  }

  private isDateEntryComplete(): boolean {
    return (this.form.get('day').touched || this.form.get('day').value)
        && (this.form.get('month').touched || this.form.get('month').value)
        && (this.form.get('year').touched || this.form.get('year').value)
  }

  notifyChanged(value: DDMMYYYY) {
    try {
      let selectedDate = DateUtils.parseDate(value);

      const valueChanged = selectedDate === null || this.value !== selectedDate;

      if (valueChanged) {
        this.value = selectedDate;
        this.stateChanges.next();
        if (this._onChange != null) {
          this._onChange(selectedDate);
          if (this.isDateEntryComplete()) {
            this._onTouched();
          }
        }
      }

    } catch(err) {
      console.error('Error converting date value', err);
    }
  }

  //#region ControlValueAccessor

  writeValue(value: DDMMYYYY | Date): void {
    if (this.form == null) {
      return;
    }
    if (value != null) {
      if (value instanceof Date) {
        const theDate = <Date> value;
        this.form.patchValue({
          day: theDate.getDate().toString().padStart(2, '0'),
          month: (theDate.getMonth() + 1).toString().padStart(2, '0'),
          year: theDate.getFullYear(),
        }, { emitEvent: false });
      } else {
        this.form.patchValue(value, { emitEvent: false });
      }
    } else {
      this.form.reset({}, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  //#endregion

  // This doesn't seem to be used...
  private isError(value: DDMMYYYY) {
    if ((value.day === "" || value.day == null) &&
        (value.month === "" || value.month == null) &&
        (value.year === "" || value.year == null))
    {
      return this.required;
    }

    if (!/[0-9]{1,2}/.test(value.day)) return true;
    if (!/[0-9]{1,2}/.test(value.month)) return true;
    if (!/[0-9]{4}/.test(value.year)) return true;

    return false;
  }

  get empty(): boolean {
    return !this.form.value || (!this.form.value.day && !this.form.value.month && !this.form.value.year)
  }

  get value() {
    return this._value;
  }

  set value(selectedDate: Date) {
    this._value = selectedDate;
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.form.disable() : this.form.enable();
    this.stateChanges.next();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  setValue() {
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }
}

interface DDMMYYYY {
  day: string,
  month: string,
  year: string
}

function coerceBooleanProperty(value: any): boolean {
  return value != null && `${value}` !== 'false';
}
